/**
 * VenueMapConfigPanel — SuperAdmin view/edit of venue-map station config.
 * Numeric percentage fields only (no drag editor). All changes audited server-side.
 */
import { useEffect, useState } from "react";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { MapPin, Save } from "lucide-react";
import { clearMapConfigCache } from "../data/venueMapConfig";

export default function VenueMapConfigPanel() {
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});

  const refresh = async () => {
    try {
      const { data } = await api.get("/venue-map/config");
      setRows(data);
      setEdits({});
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { refresh(); }, []);

  const setEdit = (sid, field, value) =>
    setEdits((p) => ({ ...p, [sid]: { ...(p[sid] || {}), [field]: value } }));

  const save = async (row) => {
    const e = edits[row.station_id];
    if (!e) return;
    const body = {};
    if (e.x_percent !== undefined) body.x_percent = parseFloat(e.x_percent);
    if (e.y_percent !== undefined) body.y_percent = parseFloat(e.y_percent);
    if (e.active !== undefined) body.active = e.active;
    if ((body.x_percent != null && (isNaN(body.x_percent) || body.x_percent < 0 || body.x_percent > 100)) ||
        (body.y_percent != null && (isNaN(body.y_percent) || body.y_percent < 0 || body.y_percent > 100))) {
      return toast.error("Coordinates must be 0–100");
    }
    try {
      await api.put(`/venue-map/config/${row.station_id}`, body);
      clearMapConfigCache();
      toast.success(`${row.label} updated (audited)`);
      refresh();
    } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <div className="space-y-2" data-testid="venue-map-config-panel">
      <div className="flex items-center gap-2 text-[10px] tracking-luxe text-motif-muted">
        <MapPin className="w-3.5 h-3.5" /> VENUE MAP STATIONS — coordinates are % of map width/height
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-motif-muted">
              <th className="py-1 pr-2">Station ID</th>
              <th className="py-1 pr-2">Label</th>
              <th className="py-1 pr-2">X %</th>
              <th className="py-1 pr-2">Y %</th>
              <th className="py-1 pr-2">Active</th>
              <th className="py-1" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const e = edits[r.station_id] || {};
              const dirty = Object.keys(e).length > 0;
              return (
                <tr key={r.station_id} className="border-t border-motif" data-testid={`map-config-row-${r.station_id}`}>
                  <td className="py-1.5 pr-2 font-mono text-[10px]">{r.station_id}</td>
                  <td className="py-1.5 pr-2">{r.label}</td>
                  <td className="py-1.5 pr-2">
                    <input
                      className="input-motif w-16 py-1 px-2 text-xs"
                      value={e.x_percent !== undefined ? e.x_percent : (r.x_percent ?? "")}
                      placeholder="—"
                      disabled={r.x_percent == null}
                      onChange={(ev) => setEdit(r.station_id, "x_percent", ev.target.value)}
                      data-testid={`map-config-x-${r.station_id}`}
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <input
                      className="input-motif w-16 py-1 px-2 text-xs"
                      value={e.y_percent !== undefined ? e.y_percent : (r.y_percent ?? "")}
                      placeholder="—"
                      disabled={r.y_percent == null}
                      onChange={(ev) => setEdit(r.station_id, "y_percent", ev.target.value)}
                      data-testid={`map-config-y-${r.station_id}`}
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <input
                      type="checkbox"
                      checked={e.active !== undefined ? e.active : r.active !== false}
                      onChange={(ev) => setEdit(r.station_id, "active", ev.target.checked)}
                      data-testid={`map-config-active-${r.station_id}`}
                    />
                  </td>
                  <td className="py-1.5">
                    <button
                      onClick={() => save(r)}
                      disabled={!dirty}
                      className="btn-ghost text-[10px] flex items-center gap-1 disabled:opacity-30"
                      data-testid={`map-config-save-${r.station_id}`}
                    >
                      <Save className="w-3 h-3" /> Save
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
